import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Free Shipping Progress Bar Component
 * Handles smooth animations and updates for the free shipping progress bar
 */
class FreeShippingProgressComponent extends Component {
  /**
   * @type {HTMLElement | null}
   */
  #progressBar = null;

  /**
   * @type {HTMLElement | null}
   */
  #progressFill = null;

  /**
   * @type {number}
   */
  #currentProgress = 0;

  /**
   * @type {number}
   */
  #targetProgress = 0;

  /**
   * @type {number | null}
   */
  #animationId = null;

  connectedCallback() {
    super.connectedCallback();
    
    
    this.#progressBar = this.querySelector('.free-shipping-progress__bar');
    this.#progressFill = this.querySelector('.free-shipping-progress__fill');
    
    // Initialize shipping data if not already done
    this.#initializeShippingData();
    
    // Don't call initializeProgress - just calculate fresh
    this.#calculateAndDisplayProgress();

    // Listen for cart updates to smoothly animate progress changes
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Also listen for quantity changes and theme morph events
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.addEventListener('theme:morph:section', this.#handleSectionMorph);
    
    // Listen for custom event that fires after cart items component updates
    document.addEventListener('theme:cart:update', this.#handleThemeCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.#animationId) {
      cancelAnimationFrame(this.#animationId);
    }
    
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.removeEventListener('theme:morph:section', this.#handleSectionMorph);
    document.removeEventListener('theme:cart:update', this.#handleThemeCartUpdate);
  }

  /**
   * Initialize shipping data from data attributes
   */
  #initializeShippingData() {
    // Get data from attributes or window
    const thresholdData = this.dataset.shippingThresholds || window.FSPData?.shippingThresholdsJson || '';
    const defaultThreshold = parseInt(this.dataset.defaultThreshold || window.FSPData?.defaultThreshold || '0');
    const currency = this.dataset.currency || window.FSPData?.currentCurrency || 'USD';
    
    
    // Store in window for global access
    window.FSPData = window.FSPData || {
      shippingThresholdsJson: thresholdData,
      defaultThreshold: defaultThreshold,
      currentCurrency: currency,
      currencySymbol: this.dataset.currencySymbol || '$',
      settings: {
        'GBP': 800,
        'USD': 1000,
        'EUR': 800,
        'CAD': 1400,
        'AUD': 1500,
        'AED': 800
      }
    };
  }

  /**
   * Calculate and display progress immediately
   */
  #calculateAndDisplayProgress() {
    // Always fetch fresh cart data to avoid stale values
    this.#fetchCartAndUpdate();
  }

  /**
   * Fetch fresh cart data
   */
  #fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (cart && cart.total_price !== undefined) {
          this.#updateProgressDisplay(cart.total_price);
        }
      })
      .catch(error => {
        console.error('❌ Error fetching cart:', error);
      });
  }

  /**
   * Update progress display with cart total
   */
  #updateProgressDisplay(cartTotalCents) {
    const data = window.FSPData || {};
    let threshold = 0;
    let thresholdFound = false;
    
    
    // Don't hide/show the component here - let the Liquid template handle it
    
    // Try to parse thresholds JSON
    try {
      if (data.shippingThresholdsJson && data.shippingThresholdsJson.trim() !== '') {
        const thresholds = JSON.parse(data.shippingThresholdsJson);
        const currencyThreshold = thresholds.find(item => item.currency === data.currentCurrency);
        if (currencyThreshold) {
          threshold = currencyThreshold.minimum_order_amount;
          thresholdFound = true;
        }
      }
    } catch (error) {
      console.error('JSON parsing error:', error);
    }
    
    // Fallback to default or settings
    if (!thresholdFound) {
      if (data.defaultThreshold > 0) {
        threshold = data.defaultThreshold;
      } else {
        threshold = data.settings?.[data.currentCurrency] || 1000;
      }
    }
    
    // Calculate progress
    const thresholdCents = threshold * 100;
    const remainingCents = thresholdCents - cartTotalCents;
    const progressPercentage = Math.min(100, Math.round((cartTotalCents * 100) / thresholdCents));
    
    
    // Update message
    const messageElement = this.querySelector('.free-shipping-progress__message');
    const successElement = this.querySelector('.free-shipping-progress__success');
    
    if (remainingCents > 0) {
      const remainingAmount = (remainingCents / 100).toFixed(2);
      const amountDisplay = (data.currencySymbol || '$') + remainingAmount;
      const message = `You are ${amountDisplay} away from FREE SHIPPING`;
      
      if (messageElement) {
        messageElement.textContent = message;
        messageElement.style.display = 'block';
      }
      if (successElement) {
        successElement.style.display = 'none';
      }
    } else {
      if (messageElement) {
        messageElement.style.display = 'none';
      }
      if (successElement) {
        successElement.style.display = 'block';
      }
    }
    
    // Update progress bar
    if (this.#progressBar) {
      this.#progressBar.style.setProperty('--progress-percentage', progressPercentage + '%');
      this.#progressBar.setAttribute('aria-valuenow', progressPercentage);
      this.#progressBar.setAttribute('aria-label', `Free shipping progress: ${progressPercentage}%`);
    }
    
    if (this.#progressFill) {
      // Always set the width directly based on calculation
      this.#progressFill.style.width = progressPercentage + '%';
      this.#currentProgress = progressPercentage;
      this.#targetProgress = progressPercentage;
    }
    
    // Update data attributes
    this.dataset.threshold = thresholdCents;
    
    // Store in global state
    window.FSPState = window.FSPState || {};
    window.FSPState.lastCartTotal = cartTotalCents;
    window.FSPState.lastProgress = progressPercentage;
    window.FSPState.lastMessage = messageElement?.textContent || '';
  }

  /**
   * Handle quantity update events
   */
  #handleQuantityUpdate = (event) => {
    // Delay to allow cart to update on server
    setTimeout(() => {
      this.#fetchCartAndUpdate();
    }, 300);
  };

  /**
   * Handle section morph events (when cart drawer is re-rendered)
   */
  #handleSectionMorph = (event) => {
    // Small delay to ensure we're after the morph
    setTimeout(() => {
      // Check if this component still exists in DOM
      if (this.isConnected) {
        this.#calculateAndDisplayProgress();
      }
    }, 50);
  };

  /**
   * Handle theme cart update events
   */
  #handleThemeCartUpdate = (event) => {
    if (event.detail?.resource?.total_price !== undefined) {
      // Use cart total from event
      this.#updateProgressDisplay(event.detail.resource.total_price);
    } else {
      // Fetch fresh cart data
      this.#fetchCartAndUpdate();
    }
  };

  /**
   * Handle cart add events (when items are added from product page)
   */
  #handleCartAdd = (event) => {
    // When an item is added, the cart drawer opens and sections are morphed
    // We need to fetch fresh cart data after a short delay
    setTimeout(() => {
      this.#fetchCartAndUpdate();
    }, 200);
  };

  /**
   * Initialize the progress bar based on current state
   */
  #initializeProgress() {
    if (!this.#progressBar || !this.#progressFill) return;

    // Don't trust the initial HTML values - they might be stale
    // Let #calculateAndDisplayProgress set the correct values
    this.#currentProgress = 0;
    this.#targetProgress = 0;
  }

  /**
   * Handle cart updates to animate progress changes
   * @param {CustomEvent} event - Cart update event
   */
  #handleCartUpdate = (event) => {
    // If the source is cart-items-component, fetch fresh cart data immediately
    if (event.detail?.data?.source === 'cart-items-component') {
      // Short delay to ensure server has updated
      setTimeout(() => {
        this.#fetchCartAndUpdate();
      }, 100);
    } else {
      // Wait for the section to re-render, then update progress
      requestAnimationFrame(() => {
        this.#updateProgressAfterCartChange();
      });
    }
  };

  /**
   * Update progress after cart change
   */
  #updateProgressAfterCartChange() {
    // Don't rely on DOM state - just fetch fresh cart data
    this.#fetchCartAndUpdate();
  }

  /**
   * Animate progress bar to target percentage
   */
  #animateProgress() {
    if (!this.#progressFill) return;

    // For now, just set the width directly without animation
    // to eliminate animation as a source of the 100% width issue
    this.#currentProgress = this.#targetProgress;
    this.#progressFill.style.width = `${this.#targetProgress}%`;
    
    // Trigger success animation if we reached 100%
    if (this.#targetProgress >= 100) {
      this.#triggerSuccessAnimation();
    }
  }

  /**
   * Easing function for smooth animation
   * @param {number} t - Progress value between 0 and 1
   * @returns {number} Eased value
   */
  #easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Trigger success animation when free shipping is reached
   */
  #triggerSuccessAnimation() {
    if (!this.#progressFill) return;

    // Add success class for styling
    this.#progressFill.classList.add('free-shipping-progress__fill--success');
    
    // Remove success class after animation completes
    setTimeout(() => {
      this.#progressFill?.classList.remove('free-shipping-progress__fill--success');
    }, 2000);
  }

  /**
   * Update progress bar for a specific cart total
   * @param {number} cartTotal - Cart total in cents
   */
  updateProgress(cartTotal) {
    const threshold = parseInt(this.dataset.threshold || '0');
    const newProgress = Math.min((cartTotal / threshold) * 100, 100);
    
    if (newProgress !== this.#targetProgress) {
      this.#targetProgress = newProgress;
      this.#animateProgress();
    }
  }
}

// Register the component
customElements.define('free-shipping-progress', FreeShippingProgressComponent);

export default FreeShippingProgressComponent;